import * as React from 'react';
import {
  isAlphaNumericKey,
  keyCodes,
  Cell,
  Compatible,
  Uncertain,
  UncertainCompatible,
  CellTemplate,
} from '@silevis/reactgrid';
import { DropdownArrow } from 'further-ui/components/SVGIcons';

import { resolveUploadDropdownPasteValue } from '../helpers/resolve-upload-dropdown-paste';

// Define the shape of your dropdown cell (Compatible<Cell> requires value: number)
interface DropdownValue {
  label: string;
  value: string;
  /** When set (e.g. tranche rows), paste can resolve using fund/tranche name without firm prefix. */
  fundName?: string;
}

interface DropdownCell extends Cell {
  type: 'custom_dropdown';
  value: number;
  text: string;
  values: Array<DropdownValue>;
}

type CompatibleDropdownCell = Compatible<DropdownCell>;
type UncertainDropdownCell = Uncertain<DropdownCell>;

/**
 * Custom dropdown template for react-grid with single-click dropdown opening.
 * Selected value is stored in text; value is NaN to satisfy ReactGrid Compatible.
 * @see https://github.com/silevis/reactgrid/blob/develop/src/lib/Components/CellRenderer.tsx
 */
class CustomDropdownTemplate implements CellTemplate<DropdownCell> {
  private focusSelectOnEditMode(
    select: HTMLSelectElement | null,
    isInEditMode: boolean,
  ) {
    if (!select || !isInEditMode) {
      return;
    }
    window.setTimeout(() => select.focus(), 0);
  }

  /**
   * Normalizes an uncertain cell into a compatible cell. Selected option value lives in text.
   */
  getCompatibleCell(
    uncertainCell: UncertainDropdownCell,
  ): CompatibleDropdownCell {
    const raw =
      uncertainCell.text !== undefined && uncertainCell.text !== null
        ? String(uncertainCell.text)
        : uncertainCell.value !== undefined && uncertainCell.value !== null
        ? String(uncertainCell.value)
        : '';
    const values = uncertainCell.values ?? [];
    const text = resolveUploadDropdownPasteValue(raw, values);
    return {
      ...uncertainCell,
      type: 'custom_dropdown',
      text,
      value: Number.NaN,
      values,
    };
  }

  handleKeyDown(
    cell: Compatible<DropdownCell>,
    keyCode: number,
    ctrl: boolean,
    _shift: boolean,
    alt: boolean,
  ): { cell: Compatible<DropdownCell>; enableEditMode: boolean } {
    if (cell.nonEditable) {
      return { cell, enableEditMode: false };
    }

    // Never enter edit mode on click — the invisible select handles dropdown opening directly
    if (keyCode === keyCodes.POINTER) {
      return { cell, enableEditMode: false };
    }

    // Enable edit mode for alphanumeric keys (without modifiers)
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) {
      return { cell, enableEditMode: true };
    }

    // Enable edit mode for enter key
    return {
      cell,
      enableEditMode: keyCode === keyCodes.ENTER,
    };
  }

  update(
    cell: Compatible<DropdownCell>,
    cellToMerge: UncertainCompatible<DropdownCell>,
  ): Compatible<DropdownCell> {
    const text =
      cellToMerge.text !== undefined && cellToMerge.text !== null
        ? String(cellToMerge.text)
        : cell.text;
    return this.getCompatibleCell({ ...cell, text });
  }

  render(
    cell: Compatible<DropdownCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<DropdownCell>, commit: boolean) => void,
  ): React.ReactNode {
    const hasValue = Boolean(cell.text);
    const label =
      cell.values.find((o) => o.value === cell.text)?.label ?? cell.text;

    if (cell.nonEditable) {
      return (
        <div className="rg-native-dropdown-container">
          <span className="rg-native-dropdown" data-has-value={hasValue}>
            {label || ''}
          </span>
        </div>
      );
    }

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onCellChanged(
        this.getCompatibleCell({ ...cell, text: e.target.value }),
        true,
      );
    };

    const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

    // Render label text with an invisible <select> positioned over the arrow area only.
    // Clicking the arrow opens the native dropdown directly (no edit mode needed).
    // Clicking elsewhere in the cell selects it normally for copy/paste.
    return (
      <div className="rg-native-dropdown-container">
        <span className="rg-native-dropdown" data-has-value={hasValue}>
          {label || 'Please choose...'}
        </span>
        <span className="select-arrow-down" aria-hidden="true">
          <DropdownArrow color={hasValue ? '#999' : '#dddddd'} />
        </span>
        <select
          ref={(select) => this.focusSelectOnEditMode(select, isInEditMode)}
          className="rg-native-dropdown-trigger"
          value={cell.text || ''}
          onChange={handleChange}
          onPointerDown={stopPropagation}
          onCopy={stopPropagation}
          onCut={stopPropagation}
          onPaste={stopPropagation}
        >
          <option value="">Please choose...</option>
          {cell.values.map(({ label: optLabel, value }) => (
            <option key={value} value={value}>
              {optLabel}
            </option>
          ))}
        </select>
      </div>
    );
  }
}

export default CustomDropdownTemplate;
